import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import client, { setToken, clearToken, getStoredUser, setStoredUser } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(getStoredUser());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // If returning from Google OAuth, let AuthCallback handle session exchange first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // Probe /auth/me unconditionally — works for both Bearer (localStorage) and
    // httpOnly-cookie sessions. 401 = not signed in (clear local state).
    client.get("/auth/me")
      .then((r) => { setUser(r.data); setStoredUser(r.data); })
      .catch(() => { clearToken(); setUser(null); })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const r = await client.post("/auth/login", { email, password });
    setToken(r.data.token);
    setStoredUser(r.data);
    setUser(r.data);
    return r.data;
  };

  const register = async (payload) => {
    const r = await client.post("/auth/register", payload);
    setToken(r.data.token);
    setStoredUser(r.data);
    setUser(r.data);
    return r.data;
  };

  const logout = async () => {
    try {
      await client.post("/auth/logout");
    } catch (err) {
      // Best-effort: server logout can fail (network/expired) but we always clear locally.
      console.warn("Server logout failed; clearing client session anyway:", err);
    }
    clearToken();
    setUser(null);
  };

  const value = useMemo(
    () => ({ user, loading, login, register, logout }),
    [user, loading]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
